import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  Car,
  MapPin,
  CheckCircle,
  AlertCircle,
  Navigation,
  ExternalLink,
  CheckCheck,
} from 'lucide-react';
import { useGuestActionsFeed, type ActionFeedItem } from '@/hooks/useGuestActionsFeed';
import type { GuestActionType } from '@/types/database';

interface NotificationBellProps {
  landlordId: string | undefined;
}

const ACTION_ICONS: Record<GuestActionType, React.ReactNode> = {
  departed: <Car className="w-4 h-4" />,
  at_parking: <MapPin className="w-4 h-4" />,
  arrived: <CheckCircle className="w-4 h-4" />,
  location_shared: <Navigation className="w-4 h-4" />,
  help_needed: <AlertCircle className="w-4 h-4" />,
};

const ACTION_COLORS: Record<GuestActionType, string> = {
  departed: 'text-blue-500 bg-blue-500/10',
  at_parking: 'text-amber-500 bg-amber-500/10',
  arrived: 'text-emerald-500 bg-emerald-500/10',
  location_shared: 'text-cyan-500 bg-cyan-500/10',
  help_needed: 'text-red-500 bg-red-500/10',
};

function getRelativeTime(dateStr: string, locale: string): string {
  const now = Date.now();
  const date = new Date(dateStr).getTime();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) return locale === 'hr' ? 'Upravo' : 'Just now';
  if (diffMin < 60) return locale === 'hr' ? `prije ${diffMin} min` : `${diffMin} min ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return locale === 'hr' ? `prije ${diffHours} h` : `${diffHours}h ago`;
  return locale === 'hr' ? 'prije 1 d' : '1d ago';
}

export function NotificationBell({ landlordId }: NotificationBellProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const markReadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const locale = i18n.language;

  const { actions, unreadCount, markAllRead } = useGuestActionsFeed(landlordId);

  const actionLabels: Record<string, string> = {
    departed: t('actionsFeed.departed', 'Krenuo/la'),
    at_parking: t('actionsFeed.at_parking', 'Na parkingu'),
    arrived: t('actionsFeed.arrived', 'Stigao/la'),
    location_shared: t('actionsFeed.location_shared', 'Podijelio/la lokaciju'),
    help_needed: t('actionsFeed.help_needed', 'Treba pomoć'),
  };

  // Click outside to close
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: Event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('pointerdown', handleClickOutside);
    return () => document.removeEventListener('pointerdown', handleClickOutside);
  }, [isOpen]);

  // Auto mark-as-read 1.5s after opening
  useEffect(() => {
    if (isOpen && unreadCount > 0) {
      markReadTimerRef.current = setTimeout(() => {
        markAllRead();
      }, 1500);
    }
    return () => {
      if (markReadTimerRef.current) {
        clearTimeout(markReadTimerRef.current);
      }
    };
  }, [isOpen, unreadCount, markAllRead]);

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleActionClick = useCallback(
    (action: ActionFeedItem) => {
      // For location actions with GPS → open Google Maps
      if (
        (action.action_type === 'location_shared' || action.action_type === 'departed') &&
        action.latitude &&
        action.longitude
      ) {
        window.open(
          `https://www.google.com/maps?q=${action.latitude},${action.longitude}`,
          '_blank',
        );
        return;
      }
      // Otherwise → go to booking detail
      setIsOpen(false);
      navigate(`/dashboard/bookings/${action.booking_id}`);
    },
    [navigate],
  );

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={handleToggle}
        title={t('actionsFeed.title', 'Obavijesti')}
        className="relative flex items-center justify-center h-9 w-9 md:h-10 md:w-10 rounded-xl text-gray-500 hover:text-amber-600 dark:text-gray-400 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-500/10 active:scale-95 transition-all duration-200"
      >
        <Bell className="h-[20px] w-[20px]" strokeWidth={2} />

        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-gradient-to-r from-amber-500 to-orange-500 px-1 text-[10px] font-bold text-white ring-2 ring-white dark:ring-gray-950 shadow-lg shadow-amber-500/30 animate-in zoom-in duration-200">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 md:w-96 bg-white/95 dark:bg-gray-950/95 backdrop-blur-xl border border-gray-200/60 dark:border-white/[0.08] rounded-2xl shadow-2xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {t('actionsFeed.title', 'Obavijesti')}
            </h3>
            {unreadCount > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  markAllRead();
                }}
                className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-600 font-medium transition-colors"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                {t('actionsFeed.markAllRead', 'Označi sve')}
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-[400px] overflow-y-auto">
            {actions.length === 0 ? (
              <div className="py-10 text-center">
                <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                  <Bell className="w-5 h-5 text-gray-400" />
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('actionsFeed.empty', 'Nema novih obavijesti')}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-800/50">
                {actions.map((action) => {
                  const hasLocation =
                    (action.action_type === 'location_shared' || action.action_type === 'departed') &&
                    action.latitude &&
                    action.longitude;

                  return (
                    <button
                      key={action.id}
                      onClick={() => handleActionClick(action)}
                      className="w-full flex items-start gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-left"
                    >
                      {/* Unread dot */}
                      <div className="flex-shrink-0 mt-2.5">
                        {!action.is_read ? (
                          <div className="w-2 h-2 rounded-full bg-amber-500" />
                        ) : (
                          <div className="w-2 h-2" />
                        )}
                      </div>

                      {/* Action Icon */}
                      <div
                        className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
                          ACTION_COLORS[action.action_type] || 'text-gray-500 bg-gray-100'
                        }`}
                      >
                        {ACTION_ICONS[action.action_type] || <Bell className="w-4 h-4" />}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {action.guest_name}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {actionLabels[action.action_type] || action.action_type}
                          {' · '}
                          {action.unit_name}
                        </p>

                        {/* Location hint - tells user this will open map */}
                        {hasLocation && (
                          <span className="inline-flex items-center gap-1 mt-1 text-[11px] text-blue-500 font-medium">
                            <ExternalLink className="w-3 h-3" />
                            {action.action_type === 'departed'
                              ? t('actionsFeed.viewRoute', 'Prikaži rutu')
                              : t('actionsFeed.viewOnMap', 'Prikaži na karti')}
                          </span>
                        )}
                      </div>

                      {/* Time */}
                      <span className="flex-shrink-0 text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                        {getRelativeTime(action.created_at, locale)}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
